/**
 * Analytics for the Library upload step: what the user picked and why a pick
 * was refused before anything reached the server. Pure module (no RN imports)
 * so the decision is unit-testable. Never the file name: only its extension
 * and a size bucket.
 */
import { fileExtensionOf } from '../../services/analyticsPrivacy';

export type LibraryPickOutcome = 'audio' | 'text' | 'unsupported' | 'audio_too_large' | 'text_too_large';

export function sizeBucket(bytes: number | null | undefined): string | undefined {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return undefined;
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return 'lt_1mb';
  if (mb < 10) return '1_10mb';
  if (mb < 50) return '10_50mb';
  if (mb < 100) return '50_100mb';
  return 'gte_100mb';
}

export function libraryPickEvent(
  outcome: LibraryPickOutcome,
  fileName: string | null | undefined,
  sizeBytes?: number | null,
): { event: string; properties: Record<string, unknown> } {
  const base = {
    surface: 'library',
    file_extension: fileExtensionOf(fileName),
    size_bucket: sizeBucket(sizeBytes),
  };
  switch (outcome) {
    case 'audio':
    case 'text':
      return { event: 'library_file_picked', properties: { ...base, kind: outcome } };
    case 'audio_too_large':
      // Same event as the server's refusal, marked as decided on the device.
      return {
        event: 'audio_upload_rejected',
        properties: { ...base, error_code: 'audio_too_large', job_kind: 'transcription', client_side: true },
      };
    case 'text_too_large':
      return { event: 'library_file_rejected', properties: { ...base, error_code: 'file_too_large' } };
    default:
      return { event: 'library_file_rejected', properties: { ...base, error_code: 'unsupported_file_type' } };
  }
}
